import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { AppCard } from '@components/app-card';
import { Badge } from '@components/badge';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { EmptyState } from '@components/empty-state';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconDisconnect } from '@components/icons/disconnect';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTemplate } from '@components/icons/template';
import { IconTrash } from '@components/icons/trash';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PasswordField } from '@components/password-field';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { useToaster } from '@components/toaster';

import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

import {
  adManagerDisconnect,
  adManagerGetSettings,
  adManagerGetTemplates,
  adManagerRemoveTemplate,
  adManagerSaveSettings,
} from './api';

const Header = ({ connected }: { connected: boolean }) => {
  const { t } = useStore();
  return (
    <Stack space={2}>
      <Text font="heading2" text={t('company.settings.apps.admanager')} />
      <Stack space={0} align="flex-start">
        <Badge
          variant={connected ? 'green' : 'neutral'}
          text={t(`integrations.common.settings.${connected ? 'connected' : 'notconnected'}`)}
        />
      </Stack>
    </Stack>
  );
};

export const ADManagerSettingsPage = () => {
  const tokenQuery = useQuery({
    queryKey: ['ad-manager-settings'],
    queryFn: () => adManagerGetSettings(),
  });
  if (tokenQuery.isLoading) {
    return <RelativeSpinner />;
  }
  if (!tokenQuery.isSuccess) {
    return null;
  }
  return (
    <Stack space={4}>
      <Header connected={tokenQuery.data.connected ?? false} />
      <Separator />
      <Info />
      <Content
        connected={tokenQuery.data.connected ?? false}
        baseUrl={tokenQuery.data.baseUrl}
        domainName={tokenQuery.data.domainName}
      />
    </Stack>
  );
};

type ContentProps = {
  connected: boolean;
  baseUrl?: string;
  domainName?: string;
};

const Content = (props: ContentProps) => {
  const { t } = useStore();
  const [authToken, setAuthToken] = useState('');
  const [baseUrl, setBaseUrl] = useState(props.baseUrl ?? '');
  const [domainName, setDomainName] = useState(props.domainName ?? '');
  const { showSuccess, showError } = useToaster();

  const client = useQueryClient();

  const saveSettings = useMutation(() => adManagerSaveSettings(authToken, baseUrl, domainName), {
    onSuccess: () => {
      showSuccess(t('integrations.admanager.settings.save.success'));
      client.invalidateQueries(['ad-manager-settings']);
    },
    onError: () => {
      showError(t('integrations.admanager.settings.save.error'));
    },
  });
  const disconnect = useMutation(() => adManagerDisconnect(), {
    onSuccess: () => {
      showSuccess(t('integrations.admanager.settings.disconnect.success'));
      client.invalidateQueries(['ad-manager-settings']);
    },
    onError: () => {
      showError(t('integrations.admanager.settings.disconnect.error'));
    },
  });
  if (props.connected) {
    return (
      <Stack space={4}>
        <Stack space={2} orientation="horizontal" justify="flex-end">
          <Menu buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'secondary' }}>
            <Menu.Item
              icon={<IconDisconnect />}
              label={t('integrations.common.settings.disconnect')}
              onSelect={() => disconnect.mutate()}
            />
          </Menu>
        </Stack>
        <TemplateList />
      </Stack>
    );
  }
  return (
    <>
      <Text font="regular16" text={t('integrations.admanager.settings.helptext')} />
      <PasswordField
        label={t('integrations.admanager.settings.authtoken')}
        onChange={setAuthToken}
      />
      <TextField
        label={t('integrations.admanager.settings.baseurl')}
        onChange={setBaseUrl}
        defaultValue={props.baseUrl}
      />
      <TextField
        label={t('integrations.admanager.settings.domainname')}
        onChange={setDomainName}
        defaultValue={props.domainName}
      />
      <Stack space={2} orientation="horizontal" justify="flex-end">
        <Button
          variant="primary"
          text={t('common.forms.button.submit')}
          onClick={() => saveSettings.mutate()}
          isDisabled={saveSettings.isLoading || !authToken || !baseUrl || !domainName}
        />
      </Stack>
    </>
  );
};

const TemplateList = () => {
  const { t } = useStore();
  const { showModal } = useModal();
  const { showSuccess } = useToaster();
  const client = useQueryClient();
  const templatesQuery = useQuery({
    queryKey: ['ad-manager-templates'],
    queryFn: adManagerGetTemplates,
  });
  if (templatesQuery.isLoading) {
    return <RelativeSpinner />;
  }
  if (!templatesQuery.data) {
    return null;
  }
  return (
    <Stack space={4}>
      <Stack space={0} orientation="horizontal" align="center" justify="space-between">
        <Text font="bold16" text={t('integrations.admanager.settings.templates.title')} />
        <IconButton
          variant="ghost-purple"
          icon={<IconAddPlus />}
          onClick={() => showModal('INTEGRATION_ADMANAGER_ADD_TEMPLATE')}
        />
      </Stack>
      <Stack space={2}>
        {templatesQuery.data.length === 0 && (
          <EmptyState
            header={t('integrations.admanager.settings.templates.none.header')}
            content={t('integrations.admanager.settings.templates.none.content')}
            icon={<IconTemplate size={80} />}
          />
        )}
        {templatesQuery.data.map((template) => (
          <Card space={4} key={template}>
            <Stack space={0} orientation="horizontal" justify="space-between" align="center">
              <Text font="bold14" text={template} />
              <Menu buttonProps={{ variant: 'ghost', iconLeft: <IconMoreVertical /> }}>
                <Menu.Item
                  label={t('integrations.admanager.settings.templates.delete')}
                  icon={<IconTrash />}
                  variant="danger"
                  onSelect={() => {
                    adManagerRemoveTemplate(template).then(() => {
                      showSuccess(t('integrations.admanager.settings.templates.remove.success'));
                      client.resetQueries(['ad-manager-templates']);
                    });
                  }}
                />
              </Menu>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};

const Info = () => {
  const { t } = useStore();
  return (
    <>
      <Text font="bold14" text={t('integrations.common.settings.withthis')} />
      <AppCard padding="md" variant="default">
        <Stack space={2} orientation="horizontal" align={'center'}>
          <IconCheckboxChecked color="blue100" />
          <Text font="semibold14" text={t('integrations.admanager.settings.withthis.create')} />
        </Stack>
      </AppCard>
    </>
  );
};
